import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Link,
  TablePagination,
} from "@mui/material";
import { orderService } from "../services/orderService";
import { format } from "date-fns";
import { getStatusChipColor } from "../utils/statusUtils";

/**
 * Orders Page component
 * Displays a list of user orders with filtering and search capabilities
 * @returns {JSX.Element} The OrdersPage component
 */
const OrdersPage = () => {
  // Use both common and orders namespaces
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("orders");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalOrders, setTotalOrders] = useState(0);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        // API now returns { orders: [...], pagination: {...} }
        const data = await orderService.getUserOrders(page + 1, rowsPerPage);
        setOrders(data.orders || []);
        setTotalOrders(data.pagination?.total || 0);
      } catch (err) {
        setError(err.message || tCommon("status.error"));
        console.error("Fetch orders error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [page, rowsPerPage, tCommon]);

  useEffect(() => {
    document.title = t("user.list.title");
    return () => {
      document.title = "B2B E-Commerce";
    };
  }, [t]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t("user.list.title")}
      </Typography>

      {!loading && !error && orders.length === 0 && (
        <Typography sx={{ mt: 3 }}>{t("user.list.noOrders")}</Typography>
      )}

      {!loading && !error && orders.length > 0 && (
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell>{t("shared.orderDetails.orderNumber")}</TableCell>
                  <TableCell>{t("user.list.date")}</TableCell>
                  <TableCell align="right">{t("user.list.total")}</TableCell>
                  <TableCell>{t("user.list.status")}</TableCell>
                  <TableCell>
                    {t("shared.orderDetails.paymentStatus")}
                  </TableCell>
                  <TableCell align="center">{t("user.list.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow hover role="checkbox" tabIndex={-1} key={order.id}>
                    <TableCell>
                      <Link component={RouterLink} to={`/orders/${order.id}`}>
                        {order.orderNumber}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {format(new Date(order.createdAt), "PPpp")}
                    </TableCell>
                    <TableCell align="right">
                      {typeof order.total === "number" ||
                      (typeof order.total === "string" &&
                        !isNaN(parseFloat(order.total)))
                        ? parseFloat(order.total).toFixed(2)
                        : "N/A"}{" "}
                      CHF
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(
                          `shared.status.${order.status?.toLowerCase()}`,
                          order.status || t("shared.status.unknown")
                        )}
                        color={getStatusChipColor(order.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(
                          `shared.status.${order.paymentStatus?.toLowerCase()}`,
                          order.paymentStatus || t("shared.status.unknown")
                        )}
                        color={getStatusChipColor(order.paymentStatus)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        component={RouterLink}
                        to={`/orders/${order.id}`}
                        variant="outlined"
                        size="small"
                      >
                        {tCommon("buttons.details")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalOrders}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={tCommon("pagination.rowsPerPage")}
          />
        </Paper>
      )}
    </Container>
  );
};

export default OrdersPage;
